import { Fragment } from 'react';
import { cn } from '@/lib/utils';
import { diffTokens, type DeckSegment } from '@/components/TechnicalAudit/deckDiff';

const isWord = (value: string): boolean => /\S/.test(value);

const countChangedWords = (segments: DeckSegment[]): number => {
  let total = 0;
  for (const segment of segments) {
    if (!segment.changed) continue;
    const words = segment.text.match(/\S+/g);
    if (words) total += words.length;
  }
  return total;
};

const hasChange = (segments: DeckSegment[]): boolean =>
  segments.some((segment) => segment.changed && isWord(segment.text));

function Segments({ segments, mark }: { segments: DeckSegment[]; mark: string }) {
  return (
    <>
      {segments.map((segment, index) => (
        <Fragment key={index}>
          {segment.changed && isWord(segment.text) ? (
            <span className={mark}>{segment.text}</span>
          ) : (
            segment.text
          )}
        </Fragment>
      ))}
    </>
  );
}

interface DiffRowProps {
  kind: 'del' | 'add';
  segments: DeckSegment[];
}

function DiffRow({ kind, segments }: DiffRowProps) {
  const add = kind === 'add';
  return (
    <div
      className={cn(
        'relative grid grid-cols-[20px_minmax(0,1fr)] items-start',
        add ? 'pdiff-row-add' : 'pdiff-row-del',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'absolute inset-y-0 left-0 w-[3px]',
          add ? 'pdiff-bar-add' : 'pdiff-bar-del',
        )}
      />
      <span
        aria-hidden
        className={cn(
          'select-none text-center text-[11px] leading-[1.65]',
          add ? 'pdiff-ink-add' : 'pdiff-ink-del',
        )}
      >
        {add ? '+' : '−'}
      </span>
      <span className="whitespace-pre-wrap break-words pl-1 pr-3 text-sm leading-[1.65] text-foreground">
        <Segments segments={segments} mark={add ? 'deck-added' : 'deck-removed'} />
      </span>
    </div>
  );
}

export interface ParagraphDiffProps {
  before: string;
  after: string;
  location?: string | null;
  index?: number;
  total?: number;
  applied?: boolean;
  className?: string;
}

export function ParagraphDiff({
  before,
  after,
  location,
  index,
  total,
  applied = false,
  className,
}: ParagraphDiffProps) {
  const segments = diffTokens(before, after);
  const added = countChangedWords(segments.after);
  const removed = countChangedWords(segments.before);
  const showDel = before.trim().length > 0 && hasChange(segments.before);

  return (
    <div
      className={cn('overflow-hidden rounded-md border border-border bg-card', className)}
      data-testid="ta-paragraph-diff"
    >
      <div className="flex h-9 items-center gap-2 border-b border-border px-3 text-xs">
        <span className="inline-flex min-w-0 items-center gap-2">
          {typeof index === 'number' && typeof total === 'number' && (
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {index}/{total}
            </span>
          )}
          {location && <span className="truncate text-muted-foreground">{location}</span>}
        </span>
        {applied ? (
          <span className="ml-auto shrink-0 text-xs font-medium pdiff-ink-add">Applied</span>
        ) : (
          <span className="ml-auto inline-flex shrink-0 items-center gap-2 font-mono text-[11px] leading-none tabular-nums">
            <span className="pdiff-ink-add">+{added}</span>
            <span className="pdiff-ink-del">&minus;{removed}</span>
          </span>
        )}
      </div>

      <div className="relative py-2">
        <span aria-hidden className="pointer-events-none absolute inset-y-0 left-5 w-px bg-border" />
        {showDel && !applied && <DiffRow kind="del" segments={segments.before} />}
        <DiffRow kind="add" segments={segments.after} />
      </div>
    </div>
  );
}
